import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Container,
  Divider,
  Flex,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Tag,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  SimpleGrid,
} from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { useSelector } from "react-redux";

import { SURVEYS_NAV, SURVEY_SECTIONS } from "./SurveysData";

function Surveys() {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeBib, setActiveBib] = useState("");
  const [activeText, setActiveText] = useState("");
  const toast = useToast();

  const isMobile = useSelector((state) => state.windowSize.isMobile);

  // ✅ Search
  const [query, setQuery] = useState("");

  // ✅ Load more rule: only show when more than 5 exist
  const PAGE_SIZE = 5;

  const [searchLimit, setSearchLimit] = useState(PAGE_SIZE);
  const [sectionLimits, setSectionLimits] = useState(() => {
    const init = {};
    for (const s of SURVEY_SECTIONS) init[s.id] = PAGE_SIZE;
    return init;
  });

  const handleBibClick = (bib, text) => {
    setActiveBib(bib);
    setActiveText(text);
    onOpen();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(activeBib).then(() => {
      toast({
        title: "Copied to your clipboard!",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    });
  };

  const scrollToId = (id) => {
    const target = document.getElementById(id);
    const offset = 145;
    if (target) {
      const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  const onQueryChange = (val) => {
    setQuery(val);
    setSearchLimit(PAGE_SIZE); // reset paging on new search
  };

  // ✅ Flatten + filter by bibtex contents
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const out = [];
    for (const section of SURVEY_SECTIONS) {
      for (const p of section.publications) {
        const hay = (p?.bibtex || "").toLowerCase();
        if (hay.includes(q)) {
          out.push({
            sectionTitle: section.title,
            sectionId: section.id,
            pub: p,
          });
        }
      }
    }
    return out;
  }, [query]);

  const inSearchMode = query.trim().length > 0;

  // Theme tokens
  const pageBg = useColorModeValue("gray.50", "rgba(255,255,255,0.02)");
  const cardBg = useColorModeValue("white", "rgba(255,255,255,0.03)");
  const titleColor = useColorModeValue("#0F172A", "white");
  const muted = useColorModeValue("gray.600", "gray.300");
  const border = useColorModeValue("rgba(2,6,23,0.10)", "rgba(148,163,184,0.18)");
  const softBorder = border;

  // ✅ Use previous section color (green)
  const accent = "#16A34A";
  const accent2 = "#22C55E";

  // ✅ Replace "red paper" color with the BLUE we used previously for section labels
  const blueForRedPapers = useColorModeValue("#1E3A8A", "#93C5FD");

  // =========================
  // HERO HEADER (inline)
  // =========================
  function HeroHeader({ accent, accent2, softBorder }) {
    const titleColor = useColorModeValue("#0F172A", "white");
    const subColor = useColorModeValue("gray.600", "gray.300");

    const heroBg = useColorModeValue(
      "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.18), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(2,6,23,0.02), rgba(2,6,23,0.00))",
      "radial-gradient(1000px 420px at 12% 0%, rgba(34,197,94,0.20), transparent 60%), radial-gradient(900px 380px at 88% 25%, rgba(22,163,74,0.14), transparent 60%), linear-gradient(180deg, rgba(255,255,255,0.03), rgba(255,255,255,0.00))"
    );

    return (
      <Box
        pt={{ base: 8, md: 12 }}
        pb={{ base: 8, md: 10 }}
        bg={heroBg}
        borderBottom="1px solid"
        borderColor={softBorder}
      >
        <Container maxW="1200px">
          <HStack spacing={2} flexWrap="wrap">
            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("white", "rgba(255,255,255,0.06)")}
              border="1px solid"
              borderColor={softBorder}
              fontWeight="800"
            >
              University of Ottawa
            </Tag>

            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("rgba(34,197,94,0.14)", "rgba(34,197,94,0.18)")}
              color={useColorModeValue("#166534", "#86EFAC")}
              fontWeight="900"
            >
              NEXTCON Lab • SCVIC
            </Tag>

            <Tag
              size="lg"
              borderRadius="full"
              bg={useColorModeValue("rgba(2,6,23,0.04)", "rgba(255,255,255,0.06)")}
              border="1px solid"
              borderColor={softBorder}
              fontWeight="900"
            >
              Surveys & Tutorials
            </Tag>
          </HStack>

          <Heading
            mt={4}
            color={titleColor}
            fontSize={{ base: "2xl", md: "3xl", lg: "4xl" }}
            lineHeight={1.05}
            letterSpacing="tight"
          >
            Surveys &{" "}
            <Box as="span" bgClip="text" bgGradient={`linear(to-r, ${accent}, ${accent2})`}>
              Tutorials
            </Box>
          </Heading>

          <Text mt={3} color={subColor} fontSize={{ base: "md", md: "lg" }} maxW="85ch" lineHeight={1.8}>
            Curated surveys and tutorial-style resources contributed by NEXTCON Lab & SCVIC.
            Search inside BibTeX to quickly find authors, venues, keywords, or DOIs.
          </Text>
        </Container>
      </Box>
    );
  }

  // =========================
  // Publication Row (Card)
  // =========================
  const PubRow = ({ p }) => (
    <Box
      bg={cardBg}
      border="1px solid"
      borderColor={border}
      borderRadius="18px"
      p={{ base: 4, md: 5 }}
      boxShadow={useColorModeValue("0 10px 24px rgba(0,0,0,0.06)", "none")}
    >
      <Flex direction="column" gap={2}>
        <Text fontSize={{ base: "md", md: "lg" }} lineHeight="1.4">
          <Box as="span" color={muted} fontSize={{ base: "sm", md: "md" }}>
            {p.authors}{" "}
          </Box>

          <Box
            as="span"
            fontWeight="800"
            color={p.paperIsRed ? blueForRedPapers : titleColor}
          >
            {p.title}{" "}
          </Box>

          <Box as="span" color={muted} fontSize={{ base: "sm", md: "md" }}>
            {p.info}
          </Box>
        </Text>

        <Flex wrap="wrap" gap={2} pt={1} align="center">
          <Button
            size="sm"
            variant="outline"
            borderColor={border}
            borderRadius="999px"
            onClick={() => handleBibClick(p.bibtex, p.bibLabel || p.title)}
            _hover={{ borderColor: "rgba(34,197,94,0.55)" }}
          >
            BibTeX
          </Button>

          {p.doi ? (
            <Button
              as={Link}
              href={p.doi}
              isExternal
              size="sm"
              borderRadius="999px"
              bg={useColorModeValue("rgba(22,163,74,0.10)", "rgba(34,197,94,0.12)")}
              color={useColorModeValue("#166534", "#86EFAC")}
              _hover={{
                textDecoration: "none",
                bg: useColorModeValue("rgba(22,163,74,0.14)", "rgba(34,197,94,0.16)"),
              }}
            >
              DOI
            </Button>
          ) : null}
        </Flex>
      </Flex>
    </Box>
  );

  return (
    <Box bg={pageBg} minH="100vh">
      {/* HERO */}
      <HeroHeader accent={accent} accent2={accent2} softBorder={softBorder} />

      {/* CONTENT */}
      <Container maxW="1200px" py={{ base: 6, md: 8 }}>
        {/* SEARCH */}
        <Box>
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <SearchIcon opacity={0.65} />
            </InputLeftElement>

            <Input
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              placeholder="Search inside BibTeX (author, year, keywords, venue, doi...)"
              borderRadius="14px"
              borderColor={border}
              bg={useColorModeValue("white", "rgba(255,255,255,0.04)")}
              _focus={{
                borderColor: "rgba(34,197,94,0.65)",
                boxShadow: "0 0 0 1px rgba(34,197,94,0.65)",
              }}
            />

            {query.trim() ? (
              <IconButton
                aria-label="Clear"
                icon={<CloseIcon boxSize={3} />}
                ml={2}
                borderRadius="14px"
                variant="outline"
                borderColor={border}
                onClick={() => onQueryChange("")}
              />
            ) : null}
          </InputGroup>

          <Text mt={2} color={muted} fontSize="sm">
            Search matches inside BibTeX entries (case-insensitive).
          </Text>
        </Box>

        {/* NAV (only when not searching) */}
        {!inSearchMode && (
          <Box mt={6} mb={{ base: 6, md: 8 }}>
            <Text fontWeight="800" color={titleColor} mb={3}>
              Jump to section
            </Text>

            {isMobile ? (
              <Box>
                {SURVEYS_NAV.map((n) => (
                  <Button
                    key={n.id}
                    w="100%"
                    justifyContent="space-between"
                    mb={2}
                    whiteSpace="normal"
                    textAlign="left"
                    onClick={() => scrollToId(n.id)}
                    variant="outline"
                    bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
                    borderColor={border}
                    borderRadius="14px"
                    _hover={{ borderColor: "rgba(34,197,94,0.55)" }}
                  >
                    {n.label}
                  </Button>
                ))}
              </Box>
            ) : (
              <Flex wrap="wrap" gap={3}>
                {SURVEYS_NAV.map((n) => (
                  <Button
                    key={n.id}
                    onClick={() => scrollToId(n.id)}
                    variant="outline"
                    bg={useColorModeValue("white", "rgba(255,255,255,0.03)")}
                    borderColor={border}
                    borderRadius="999px"
                    _hover={{ borderColor: "rgba(34,197,94,0.55)" }}
                  >
                    {n.label}
                  </Button>
                ))}
              </Flex>
            )}
          </Box>
        )}

        {/* SEARCH MODE */}
        {inSearchMode ? (
          <Box
            mt={6}
            bg={cardBg}
            border="1px solid"
            borderColor={border}
            borderRadius="22px"
            p={{ base: 4, md: 6 }}
            boxShadow={useColorModeValue("0 12px 30px rgba(0,0,0,0.06)", "none")}
          >
            <Flex justify="space-between" align="baseline" flexWrap="wrap" gap={3} mb={4}>
              <Heading fontSize={{ base: "lg", md: "xl" }} color={titleColor} letterSpacing="tight">
                Filtered results
              </Heading>
              <Text color={muted} fontSize="sm" fontWeight="800">
                {filtered.length} match{filtered.length === 1 ? "" : "es"}
              </Text>
            </Flex>

            <Divider borderColor={border} mb={5} />

            {filtered.length === 0 ? (
              <Text color={muted}>No matches found in BibTeX.</Text>
            ) : (
              <>
                <Flex direction="column" gap={4}>
                  {filtered.slice(0, searchLimit).map((x, idx) => (
                    <Box key={`${x.sectionId}-${idx}`}>
                      {/* ✅ section label uses previous section color (green) */}
                      <Text fontWeight="900" color={useColorModeValue("#166534", "#86EFAC")} mb={2}>
                        {x.sectionTitle}
                      </Text>
                      <PubRow p={x.pub} />
                    </Box>
                  ))}
                </Flex>

                {/* ✅ only show load more if more than 5 results exist */}
                {filtered.length > PAGE_SIZE && searchLimit < filtered.length ? (
                  <Flex justify="center" mt={6}>
                    <Button
                      onClick={() => setSearchLimit((v) => v + PAGE_SIZE)}
                      borderRadius="999px"
                      variant="outline"
                      borderColor={border}
                      _hover={{ borderColor: "rgba(34,197,94,0.55)" }}
                    >
                      Load more
                    </Button>
                  </Flex>
                ) : null}
              </>
            )}
          </Box>
        ) : (
          // NORMAL MODE
          <Flex direction="column" gap={{ base: 8, md: 10 }} mt={8}>
            {SURVEY_SECTIONS.map((section) => {
              const limit = sectionLimits[section.id] ?? PAGE_SIZE;
              const total = section.publications.length;
              const shown = section.publications.slice(0, limit);

              return (
                <Box key={section.id}>
                  <Flex direction="column" gap={3} mb={4}>
                    {/* ✅ section title uses previous section green */}
                    <Heading
                      id={section.id}
                      as="h2"
                      fontSize={{ base: "lg", md: "xl" }}
                      color={accent}
                      letterSpacing="tight"
                    >
                      {section.id === "DRO" && isMobile
                        ? "DISASTER RESILIENCE & OPTICAL ACCESS NETWORKS"
                        : section.title}
                    </Heading>
                    <Divider borderColor={border} />
                  </Flex>

                  <SimpleGrid columns={{ base: 1, md: 1 }} spacing={4}>
                    {shown.map((p, idx) => (
                      <PubRow key={`${section.id}-${idx}`} p={p} />
                    ))}
                  </SimpleGrid>

                  {/* ✅ only show load more if more than 5 papers exist */}
                  {total > PAGE_SIZE && limit < total ? (
                    <Flex justify="center" mt={5}>
                      <Button
                        onClick={() =>
                          setSectionLimits((prev) => ({
                            ...prev,
                            [section.id]: (prev[section.id] ?? PAGE_SIZE) + PAGE_SIZE,
                          }))
                        }
                        borderRadius="999px"
                        variant="outline"
                        borderColor={border}
                        _hover={{ borderColor: "rgba(34,197,94,0.55)" }}
                      >
                        Load more
                      </Button>
                    </Flex>
                  ) : null}
                </Box>
              );
            })}
          </Flex>
        )}
      </Container>

      {/* MODAL */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="820px" maxH="650px" borderRadius="18px">
          <ModalHeader fontSize="lg">BibTeX Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Box fontSize="md" mb={3} fontWeight="800" color={titleColor}>
              {activeText}
            </Box>
            <Box
              fontFamily="mono"
              whiteSpace="pre-wrap"
              fontSize="sm"
              lineHeight="1.5"
              p={3}
              borderRadius="12px"
              bg={useColorModeValue("gray.50", "rgba(255,255,255,0.04)")}
              border="1px solid"
              borderColor={border}
            >
              {activeBib}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="green" onClick={handleCopy} mr={3}>
              Copy
            </Button>
            <Button variant="ghost" onClick={onClose}>
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default Surveys;